import React, { useCallback, useMemo, useState } from 'react';
import { TitleH3 } from 'components/Title/TitleH3';

import ClipboardCopy from 'components/ClipboardCopy';
import { buildLinkToCandidateAuth, linkToCopy } from 'functions/linkUtils';

import candidateService from 'store/candidate/service';
import { useParams } from 'react-router';
import { AnimatedLoaderModal } from 'components/Loaders/AnimatedLoader';
import { ICandidatePageDocument } from 'common/document/interfaces/candidate-page-document';
import { LogEvents } from 'components/Logs/events';
import {
  DIALOG_TYPES,
  useGlobalDialogContext,
} from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import { DocumentReviewContainer } from 'features/Dashboard/DocumentView/DocumentReviewContainer';
import { DocumentStatusEnum } from 'common/document/enums/document-status.enum';

import { ReactComponent as ChevronDownIcon } from 'assets/images/chevron-down.svg';
import { ReactComponent as MessageIcon } from 'assets/images/message.svg';
import { ReactComponent as EditIcon } from 'assets/images/edit.svg';
import { ReactComponent as FireIcon } from 'assets/images/fire.svg';

import styles from './styles.module.scss';
import { TooltipEmployeeId } from '../../../components/TooltipEmployeeId';
import { DriverProfileDialog } from '../../../features/Driver/DriverProfile/DriverProfileDialog';
import { IDriverProfile } from '../../../features/Driver/DriverProfile/DriverProfileForm';
import PositionedMenu from '../../../components/Menu/PositionedMenu';
import { IOptionIcon } from '../../../common/ui/interfaces/selectors';
import { DocumentRequestEventNameEnum } from '../../../common/event/enums/document-request-event-name.enum';
import { DriverDocumentsTable } from '../../../features/Dashboard/DriverDocumentsTable';
import { CandidateDocumentToTableRow } from '../../../features/Dashboard/DriversTable/mappers/CandidateDocumentToTableRow';
import { useManager } from '../../../contexts/access/ManagerContext/useManager';

enum EAction {
  REQUEST_DOCUMENT = 'REQUEST_DOCUMENT',
  EDIT_DRIVER = 'EDIT_DRIVER',
  PURGE_DRIVER = 'PURGE_DRIVER',
}

export const listOfActions: IOptionIcon[] = [
  {
    label: 'Request a Document',
    value: EAction.REQUEST_DOCUMENT,
    icon: <MessageIcon />,
  },
  {
    label: 'Edit Driver Profile',
    value: EAction.EDIT_DRIVER,
    icon: <EditIcon />,
  },
  {
    label: 'Purge',
    value: EAction.PURGE_DRIVER,
    icon: <FireIcon />,
  },
];

export const DriversReviewPage = () => {
  const { id: candidateId } = useParams<{ id: string }>();
  const { showDialog } = useGlobalDialogContext();
  const { isReadOnly } = useManager();

  const [openReviewDialog, setOpenReviewDialog] = useState<boolean>(false);
  const [openEditDriverDialog, setOpenEditDriverDialog] =
    useState<boolean>(false);
  const [documentRequestId, setDocumentRequestId] = useState<string>('');
  const [candidateDocumentReviewId, setCandidateDocumentReviewId] =
    useState<string>('');

  const { data: candidatePageInfo, isFetching: isFetchingCandidatePageInfo } =
    candidateService.endpoints.getCandidatePageInfo.useQuery(
      {
        candidateId: candidateId || '',
      },
      {
        skip: !candidateId,
      }
    );

  const events = useMemo(() => {
    return (
      candidatePageInfo?.events.filter(
        (log) =>
          log.eventName !== DocumentRequestEventNameEnum.DOCUMENT_ARCHIVED
      ) || []
    );
  }, [candidatePageInfo?.events]);

  const [updateDriver, { isLoading: isLoadingUpdateDriver }] =
    candidateService.endpoints.updateDriver.useMutation();

  const handleReview = async (item: ICandidatePageDocument): Promise<void> => {
    setDocumentRequestId(item.documentRequestId);
    setCandidateDocumentReviewId(item.candidateDocumentId);
    setOpenReviewDialog(true);
  };

  const handleManage = async (item: ICandidatePageDocument): Promise<void> => {
    setDocumentRequestId(item.documentRequestId);
    setCandidateDocumentReviewId(item.candidateDocumentId);
    setOpenReviewDialog(true);
  };

  const handleSendReminder = async (
    item: ICandidatePageDocument
  ): Promise<void> => {
    const request = {
      id: item.documentRequestId,
      candidateId: candidatePageInfo?.candidateId || '',
      candidateFullName: candidatePageInfo?.candidateFullName || '',
      candidateCode: candidatePageInfo?.candidateCode || '',
      candidatePhone: candidatePageInfo?.candidatePhone || '',
      documentsNeeded: [item.documentType],
    };
    showDialog(DIALOG_TYPES.CONFIRM_REQUEST_DIALOG, {
      request,
      title: 'Confirm Reminder',
    });
  };

  const handlePurgeDriver = async (): Promise<void> => {
    showDialog(DIALOG_TYPES.CONFIRM_PURGE_DRIVER_GLOBAL_DIALOG, {
      candidateFullName: candidatePageInfo?.candidateFullName || '',
      candidateId: candidatePageInfo?.candidateId || '',
    });
  };

  const handleUpdateDriver = async (driver: IDriverProfile) => {
    updateDriver({
      candidateId: driver.id,
      candidateFirstName: driver.firstName,
      candidateLastName: driver.lastName,
      candidatePhone: driver.phone,
      candidateEmail: driver.email,
      employeeId: driver.employeeId,
    });
    setOpenEditDriverDialog(false);
  };

  const handleRemoveDocument = async (
    item: ICandidatePageDocument
  ): Promise<void> => {
    showDialog(DIALOG_TYPES.CONFIRM_PURGE_DOCUMENT_FOREVER_GLOBAL_DIALOG, {
      document: {
        ...item,
      },
      candidateFullName: candidatePageInfo?.candidateFullName || '',
    });
  };

  const isDeleted = useMemo(() => {
    return candidatePageInfo?.documents.every(
      (doc) => doc.status === DocumentStatusEnum.DELETED
    );
  }, [candidatePageInfo]);

  const handleOpenCreateRequestDialog = useCallback(() => {
    showDialog(DIALOG_TYPES.CREATE_REQUEST_DIALOG, {
      params: {
        candidateFirstName: candidatePageInfo?.candidateFirstName || '',
        candidateLastName: candidatePageInfo?.candidateLastName || '',
        candidatePhone: candidatePageInfo?.candidatePhone || '',
        candidateEmail: candidatePageInfo?.candidateEmail || '',
        candidateEmployeeId: candidatePageInfo?.employeeId || '',
        disableCandidateFields: true,
      },
    });
  }, [candidatePageInfo]);

  const handleActionSelected = useCallback(
    async (action: IOptionIcon) => {
      switch (action.value.toString()) {
        case EAction.REQUEST_DOCUMENT.toString():
          handleOpenCreateRequestDialog();
          break;
        case EAction.EDIT_DRIVER.toString():
          setOpenEditDriverDialog(true);
          break;
        case EAction.PURGE_DRIVER.toString():
          await handlePurgeDriver();
          break;
      }
    },
    [candidatePageInfo]
  );

  if (!candidatePageInfo) {
    return <AnimatedLoaderModal isLoading={isFetchingCandidatePageInfo} />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <TitleH3 className={styles.h3}>
            <div className={styles.title}>
              <div>{candidatePageInfo.candidateFullName}</div>
              <TooltipEmployeeId
                employeeId={candidatePageInfo.employeeId || ''}
                fontSize={'20px'}
              />
            </div>
          </TitleH3>
          <div className={styles.actions}>
            {!isReadOnly && (
              <PositionedMenu
                items={listOfActions}
                onSelected={handleActionSelected}
                classes={{
                  buttonClass: styles.menuActions,
                }}
                endIcon={<ChevronDownIcon />}
                listOfDisabled={isDeleted ? [EAction.PURGE_DRIVER] : []}
              >
                Actions
              </PositionedMenu>
            )}
          </div>
        </div>
        <DriverDocumentsTable
          isSearch={isFetchingCandidatePageInfo}
          withPagination={false}
          rowHeight={48}
          listOfAllCandidateDocuments={CandidateDocumentToTableRow({
            items: candidatePageInfo?.listOfAllCandidateDocuments || [],
            onReview: handleReview,
            onManage: handleManage,
            onSendReminder: handleSendReminder,
            onRemoveDocument: handleRemoveDocument,
          })}
          {...CandidateDocumentToTableRow({
            items: candidatePageInfo?.documents || [],
            onReview: handleReview,
            onManage: handleManage,
            onSendReminder: handleSendReminder,
            onRemoveDocument: handleRemoveDocument,
          })}
        />
      </div>
      <div className={styles.footer}>
        <LogEvents
          logs={events}
          align="flex-start"
          hideLogsAfterRetentionPeriodDays={60}
        />
        {candidatePageInfo?.candidateCode && (
          <ClipboardCopy
            label="Driver Unique Upload Link"
            isActive={true}
            isTransparent={true}
            copyText={linkToCopy(
              buildLinkToCandidateAuth(candidatePageInfo?.candidateCode)
            )}
          />
        )}
      </div>
      <DocumentReviewContainer
        openReviewDialog={openReviewDialog}
        setOpenReviewDialog={setOpenReviewDialog}
        documentRequestId={documentRequestId}
        selectedCandidateDocumentId={candidateDocumentReviewId}
        setSelectedCandidateDocumentId={setCandidateDocumentReviewId}
      />
      {!!candidatePageInfo && (
        <DriverProfileDialog
          open={openEditDriverDialog}
          isLoading={isLoadingUpdateDriver}
          setOpen={setOpenEditDriverDialog}
          onSubmit={handleUpdateDriver}
          driver={{
            id: candidatePageInfo.candidateId || candidateId || '',
            firstName: candidatePageInfo.candidateFirstName?.trim() || '',
            lastName: candidatePageInfo.candidateLastName?.trim() || '',
            phone: candidatePageInfo.candidatePhone,
            email: candidatePageInfo.candidateEmail || '',
            employeeId: candidatePageInfo.employeeId || '',
          }}
        />
      )}
      {/* This is for testing. Don't remove it */}
      {/*<div style={{ width: '100%', height: '1000px' }}>*/}
      {/*  <PDFViewer width="100%" height="1000px">*/}
      {/*    <DriverLicensePdf*/}
      {/*      driverName={'John Monroe'}*/}
      {/*      employeeId={'AX423423'}*/}
      {/*      snn={'AX423423'}*/}
      {/*      ticketId={'HAUL-1212'}*/}
      {/*      dateOnboarded={'2024-10-10'}*/}
      {/*      firstImage={''}*/}
      {/*      secondImage={''}*/}
      {/*      photoIdDocumentImage={''}*/}
      {/*    />*/}
      {/*  </PDFViewer>*/}
      {/*</div>*/}
    </div>
  );
};
